import React from 'react';
import { useTranslation } from "react-i18next";
import { LocalDateTime, DateTimeFormatter } from "@js-joda/core";
import PlanName from "./PlanName.jsx";
import BillingCycle from "./BillingCycle.jsx";
import {useRenderNumber} from "../../../api/utilities/useValueRenderers.js";
import Button from "../../../components/Button.jsx";
import PriceExcludesVatNote from "./PriceExcludesVatNote.jsx";

const CurrentPlanBox = ({ project, currencyCode, onCancel, cancelling = false }) => {
    const { t } = useTranslation();
    const subscription = project.subscription;
    const subscriptionPackage = subscription?.currentPackage;

    const renderNumber = useRenderNumber();

    if (!subscription || !subscriptionPackage) {
        return null;
    }

    const billingCycle = subscription.billingCycle;
    const currencySuffix = currencyCode === 'EUR' ? 'Eur' : 'Czk';
    const currencySymbol = currencyCode === 'EUR' ? '€' : 'Kč';

    let price = renderNumber(subscriptionPackage[`monthly${currencySuffix}`]);
    if (billingCycle === 'annual') {
        price = renderNumber(subscriptionPackage[`yearly${currencySuffix}`] * 12);
    }

    const isTrial = subscription.isFreeTrial;

    const expires = LocalDateTime.parse(subscription.endDate);
    const expiresString = expires.format(DateTimeFormatter.ofPattern('dd.MM.yyyy'));

    const nextPackage = subscription.nextPackage || subscriptionPackage;
    const nextBillingCycle = subscription.nextBillingCycle || billingCycle;

    let nextPrice = renderNumber(nextPackage[`monthly${currencySuffix}`]);
    if (nextBillingCycle === 'annual') {
        nextPrice = renderNumber(nextPackage[`yearly${currencySuffix}`] * 12);
    }

    const subscriptionText = isTrial
        ? t('subscription.free-trial-ends-on')
        : t('subscription.current-subscription-ends-on');

    return (
        <>
            <div className="text-xs font-bold text-gray-900 align-top mb-1">{t('subscription.active-package')}:</div>
            <div
                className="app-surface mb-8 h-full w-full rounded-xl border border-violet-300 bg-violet-50/70 px-4 py-3 text-left shadow-sm ring-1 ring-violet-100 transition dark:border-violet-500/50 dark:bg-violet-500/10 dark:ring-violet-500/20">

                <div className="flex flex-wrap items-center justify-between mb-0.5">
                    <div className={'flex gap-1'}>
                        <PlanName
                            planCode={subscriptionPackage.code}
                            className={'font-semibold text-md text-gray-800 dark:text-gray-100'}
                        />
                    </div>
                    <span className={'flex items-baseline'}>
                        <span className="font-medium text-gray-900 dark:text-white">{price} {currencySymbol}</span>
                        <BillingCycle
                            billingCycle={billingCycle}
                            className={'text-sm font-thin text-gray-500'}
                            text={'/'}
                        />
                    </span>
                </div>
                <div className="text-xs mt-2">
                    {subscriptionText} <strong>{expiresString}</strong>,
                    {subscriptionPackage.id === nextPackage.id ? (
                        <span> {t('subscription.afterwards-will-renew-for')} </span>
                    ) : (
                        <span> {t('subscription.afterwards-package-will-change-to')} <strong><PlanName planCode={nextPackage.code} /></strong> {t('subscription.and-extended-for')} </span>
                    )}
                    <strong>{nextPrice} {currencySymbol}.</strong>
                </div>
                {subscription.cancelAtPeriodEnd ? (
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                        <PriceExcludesVatNote/>
                        <div className="text-sm font-medium text-amber-600">
                            {t('subscription.cancellation-scheduled', {defaultValue: 'Předplatné je zrušené ke konci aktuálního období.'})}
                        </div>
                    </div>
                ) : (
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <PriceExcludesVatNote/>
                        <Button size="small" variant="outline-primary" disabled={cancelling} onClick={onCancel}>
                            {t('subscription.cancel-at-period-end', {defaultValue: 'Zrušit předplatné'})}
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
};

export default CurrentPlanBox;
